﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import { ensureSubject } from "~base/Services/API/Subject/EnsureSubject";
import { useService } from "~base/Services/ServiceContext";
import * as api from "~base/Services/API";
import * as sessionModel from "~base/Services/API/Session/Model";

//-------------------------------------------------------------------------------------
// Http Contract
//-------------------------------------------------------------------------------------
const uri = "api/Guide/v1/Sessions";
const method = "POST";

//-------------------------------------------------------------------------------------
// Types
//-------------------------------------------------------------------------------------

export interface CreateSessionEnsureSubjectRequest {
    sessionTemplateId: number;
    subjectIdentifierType: string;
    subjectIdentifier: string;
    subjectType: string;
    locale: string;
    externalSessionIdentifier?: string;
    initialInfRules?: Array<sessionModel.InitialInfRuleRequest>;
    salesOpportunity?: string;
}

interface CreateSessionProps extends api.ApiHookBaseProps<
    CreateSessionEnsureSubjectRequest,
    sessionModel.CreateSessionResponse,
    unknown
> {}

//-------------------------------------------------------------------------------------
// API Hook
//-------------------------------------------------------------------------------------

export function useCreateSessionEnsureSubject(
    props: CreateSessionProps
): [boolean, (data: CreateSessionEnsureSubjectRequest) => Promise<unknown>] {
    const { call, callback } = props;
    const { apiContext } = useService();

    const [loading, setLoading] = useState(false);

    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const postSessions = useCallback(
        async (
            calledExternally: boolean,
            extData: CreateSessionEnsureSubjectRequest
        ) => {
            if ((call || calledExternally) && loading == false) {
                setLoading(true);
                const data = calledExternally ? extData : props.data;
                const [status, response, errorResponse] =
                    await createSessionEnsureSubject(apiContext.adapter, data);

                if (mounted.current)
                    await callback(
                        status,
                        errorResponse,
                        response,
                        data,
                        null,
                        props.traceContext
                    );
                if (mounted.current) setLoading(false);
            }
        },
        [
            apiContext.adapter,
            loading,
            call,
            callback,
            props.data,
            props.traceContext,
        ]
    );

    useEffect(() => {
        postSessions(false, null);
    }, [postSessions]);

    return [loading, (data) => postSessions(true, data)];
}

//-------------------------------------------------------------------------------------
// API methods
//-------------------------------------------------------------------------------------

export const createSessionEnsureSubject = async (
    apiAdapter: api.ApiAdapter,
    data: CreateSessionEnsureSubjectRequest
): Promise<
    [
        status: api.ApiStatus,
        response: sessionModel.CreateSessionResponse,
        errorResponse?: Response,
    ]
> => {
    const [subjectId, ensureSubjectError] = await ensureSubject(
        apiAdapter,
        data.subjectType,
        data.subjectIdentifier,
        data.subjectIdentifierType
    );

    if (ensureSubjectError) {
        return ["Failed", null, ensureSubjectError];
    } else {
        const createSessionRequest: sessionModel.CreateSessionRequest = {
            subjectId: subjectId,
            externalSessionIdentifier: data.externalSessionIdentifier,
            locale: data.locale,
            sessionTemplateId: data.sessionTemplateId,
            initialInfRules: data.initialInfRules,
            salesOpportunity: data.salesOpportunity,
        };
        const response = await api.invokeApi(
            apiAdapter,
            uri,
            method,
            false,
            createSessionRequest
        );

        if (response.status >= 200 && response.status <= 299) {
            const responseData: sessionModel.CreateSessionResponse =
                await response.json();
            return ["Success", responseData, response];
        } else {
            return ["Failed", null, response];
        }
    }
};
